import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  UsersRound,
} from "lucide-react";
import { useCity } from "../state/CityContext";
import { DURATION, STEP } from "../simulation/types";
import { number } from "./Primitives";
export function Signal() {
  const { state, current, history, baseline, comparison } = useCity();
  const line = (
    samples: typeof history,
    key: "continuity" | "mobility" | "energy",
  ) =>
    samples
      .map(
        (s, i) =>
          `${i ? "L" : "M"}${8 + (s.minute / DURATION) * 262},${16 + (100 - s[key]) * 0.75}`,
      )
      .join("");
  const baselineNow = baseline[state.run.minute / STEP];
  const benefit = current.continuity - baselineNow.continuity;
  const prior = comparison?.history[state.run.minute / STEP];
  return (
    <aside className="signal-panel" aria-label="City analytics">
      <div className="signal-heading">
        <span className="eyebrow">
          <Activity size={12} /> CITY SIGNAL
        </span>
        <span className="mini-label">continuity</span>
      </div>
      <div className="signal-value">
        <strong>
          {Math.round(current.continuity)}
          <small>%</small>
        </strong>
        <span>
          {current.continuity > 90
            ? "A city in balance"
            : current.continuity > 70
              ? "Pressure is building"
              : current.continuity > 50
                ? "Systems under strain"
                : "Continuity at risk"}
        </span>
      </div>
      <svg
        className="signal-chart"
        viewBox="0 0 280 107"
        role="img"
        aria-label={`Continuity trajectory over 120 simulated minutes. Current ${Math.round(current.continuity)} per cent. Grey dashed line shows no response.`}
      >
        <defs>
          <clipPath id="past-chart">
            <rect
              x="0"
              y="0"
              width={8 + (state.run.minute / DURATION) * 262}
              height="110"
            />
          </clipPath>
        </defs>
        {[16, 53.5, 91].map((y) => (
          <line
            key={y}
            x1="8"
            x2="270"
            y1={y}
            y2={y}
            stroke="#3b4a50"
            opacity=".45"
            strokeDasharray="2 4"
          />
        ))}
        <path
          d={line(baseline, "continuity")}
          fill="none"
          stroke="#a1aaad"
          strokeWidth="1.2"
          strokeDasharray="4 4"
        />
        {comparison && (
          <path
            d={line(comparison.history, "continuity")}
            fill="none"
            stroke="#cbb5ef"
            strokeWidth="1.5"
            opacity=".8"
          />
        )}
        <path
          d={line(history, "continuity")}
          fill="none"
          stroke="#c7ed94"
          opacity=".24"
          strokeWidth="2"
        />
        <path
          d={line(history, "continuity")}
          fill="none"
          stroke="#c7ed94"
          strokeWidth="2"
          clipPath="url(#past-chart)"
        />
        <line
          x1={8 + (state.run.minute / DURATION) * 262}
          x2={8 + (state.run.minute / DURATION) * 262}
          y1="8"
          y2="98"
          stroke="#c7ed94"
          opacity=".4"
        />
        <circle
          cx={8 + (state.run.minute / DURATION) * 262}
          cy={16 + (100 - current.continuity) * 0.75}
          r="3.5"
          fill="#d7f4b0"
        />
        <text x="8" y="106" fill="#a8b7bb" fontSize="9">
          08:00
        </text>
        <text x="243" y="106" fill="#a8b7bb" fontSize="9">
          10:00
        </text>
      </svg>
      <div className="chart-legend">
        <span>
          <i />
          Your plan
        </span>
        <span>
          <i className="baseline" />
          No response
        </span>
        {comparison && (
          <span>
            <i className="pinned" />
            Pinned
          </span>
        )}
      </div>
      <div className="signal-benefit">
        {benefit >= 0 ? (
          <ArrowUpRight size={15} />
        ) : (
          <ArrowDownRight size={15} />
        )}
        <span>
          {benefit > 0
            ? `+${benefit.toFixed(1)} points from your response`
            : state.run.scenario === "calm"
              ? "Introduce a scenario to test resilience"
              : "No response benefit at this time"}
        </span>
      </div>
      {prior && (
        <div className="compare-inline">
          {current.continuity - prior.continuity >= 0 ? "+" : ""}
          {(current.continuity - prior.continuity).toFixed(1)} points vs pinned
          plan at this time
        </div>
      )}
      <div className="affected-count">
        <UsersRound size={15} />
        <strong>{number(current.affected)}</strong>
        <span>households affected</span>
      </div>
    </aside>
  );
}
export function Metrics() {
  const { current } = useCity();
  return (
    <div className="metrics" aria-label="Simulation system health">
      <div>
        <span className="metric-dot mobility" />
        <span>Mobility</span>
        <strong>
          {Math.round(current.mobility)}
          <small>%</small>
        </strong>
      </div>
      <div>
        <span className="metric-dot power" />
        <span>Energy</span>
        <strong>
          {Math.round(current.energy)}
          <small>%</small>
        </strong>
      </div>
      <div>
        <span className="metric-dot continuity" />
        <span>Continuity</span>
        <strong>
          {Math.round(current.continuity)}
          <small>%</small>
        </strong>
      </div>
    </div>
  );
}
